import { ComponentProps, ElementType } from 'react';
import { NavLink } from 'react-router-dom';

import { ListItemProps, styled } from '@mui/material';
import ButtonBase from '@mui/material/ButtonBase';
// A deliberate reach into the visual-refresh directory: when the flag retires and that directory is
// deleted, this import breaks loudly and forces the branch below to be resolved.
import {
  BACKGROUND_ACCENT_MUTED,
  BACKGROUND_ACCENT_MUTED_ACTIVE,
  BACKGROUND_ACCENT_MUTED_HOVER,
  BACKGROUND_PRIMARY_ACTIVE,
  BACKGROUND_PRIMARY_HOVER,
  FOREGROUND_ACCENT,
  FOREGROUND_ACCENT_ACTIVE,
  FOREGROUND_ACCENT_HOVER,
} from 'common/theme/refresh/tokens';
import { omitForwardedProps } from 'common/utils';

export type NavigationItemSize = 'small' | 'default';

type Props = ListItemProps & ComponentProps<typeof NavLink> & { component?: ElementType; size: NavigationItemSize };

/**
 * The sidebar's navigation row. Its colours are literals here rather than theme roles, so the theme
 * swap cannot reach them and the refresh branches per value: the resting, hovered and pressed rows
 * take the `background/primary` states, and the current page takes the `accent-muted` wayfinding
 * surface with `foreground/accent` on it, as Figma defines that pair. Resting text is
 * `text.secondary` in both themes, which the legacy palette already sets to this row's `grey[600]`.
 */
export const NavigationItem = styled(ButtonBase, {
  shouldForwardProp: omitForwardedProps('size'),
})<Props>(({ theme: { palette, spacing, shape, visualRefresh }, size }) => ({
  justifyContent: size === 'small' ? 'center' : 'start',
  display: 'flex',
  gap: spacing(1.5),
  alignItems: 'center',

  margin: spacing(0.25, 0),
  padding: spacing(0.75, 1),
  borderRadius: shape.borderRadius * 2,

  transition: 'all .2s cubic-bezier(.17,.67,.83,.67)',

  color: palette.text.secondary,

  '& .MuiSvgIcon-root': {
    fontSize: '1.25rem',
    color: 'inherit',
  },

  '&:hover': {
    backgroundColor: visualRefresh ? BACKGROUND_PRIMARY_HOVER : palette.grey[100],
  },

  '&:focus-visible': {
    backgroundColor: visualRefresh ? BACKGROUND_PRIMARY_ACTIVE : palette.grey[300],
    outline: 'none',
    '& svg': {
      color: palette.text.secondary,
    },
  },

  '&.active': {
    color: visualRefresh ? FOREGROUND_ACCENT : palette.primary.main,
    background: visualRefresh ? BACKGROUND_ACCENT_MUTED : palette.extendedPrimary[50],

    '&:hover': {
      backgroundColor: visualRefresh ? BACKGROUND_ACCENT_MUTED_HOVER : palette.extendedPrimary[100],
      color: visualRefresh ? FOREGROUND_ACCENT_HOVER : palette.extendedPrimary[700],
    },

    '&:focus-visible': {
      backgroundColor: visualRefresh ? BACKGROUND_ACCENT_MUTED_ACTIVE : palette.extendedPrimary[300],
      color: visualRefresh ? FOREGROUND_ACCENT_ACTIVE : palette.extendedPrimary[800],
      '& svg': {
        color: visualRefresh ? FOREGROUND_ACCENT_ACTIVE : palette.extendedPrimary[800],
      },
    },
  },
}));
